'use client';

import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styled, { css } from 'styled-components';
import { colorBrandBase, formBoxShadow, boxShadowStandard, token, borderRadiusStandard, colorWhite } from '@nib-components/theme';
import { Box, Columns, Column, Stack, p, breakpoint } from '@nib/layout';
import Heading from '@nib-components/heading';
import { InfoSystemIcon } from '@nib/icons';
export const validTipLocationValues = ['left', 'center', 'right'];
const getTipAlignment = alignment => {
  if (alignment === 'right') {
    return `
      margin-inline: auto 1rem;
    `;
  } else if (alignment === 'center') {
    return `
      margin-inline: auto;
    `;
  }
  return `margin-inline: 2rem 0;`;
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const InfoBoxContainer = styled(_ref => {
  let {
    withBorder,
    iconAndBorderColor,
    withBoxShadow,
    theme,
    ...rest
  } = _ref;
  return /*#__PURE__*/_jsx("div", {
    ...rest
  });
})`
  position: relative;
  overflow: hidden;
  ${p({
  xs: 4,
  mini: 5
})};

  /* Additional .25rem for the absolutely positioned border below. */
  /* We apply this even when no border is rendered because the text indentation should be consistent */
  /* across InfoBoxes with and without the left border. */
  padding-inline-start: 1.25rem;
  ${breakpoint('mini')`
    padding-inline-start: 1.75rem;  
  `};

  ${_ref2 => {
  let {
    withBorder,
    iconAndBorderColor
  } = _ref2;
  return withBorder && css`
      ::before {
        content: '';
        display: block;
        inline-size: 0.25rem;
        position: absolute;
        inset-block: 0;
        inset-inline-start: 0;
        background-color: ${iconAndBorderColor ? iconAndBorderColor : token('theme.color.border.brand', colorBrandBase)};
      }
    `;
}}

  ${_ref3 => {
  let {
    withBoxShadow
  } = _ref3;
  return withBoxShadow && css`
      box-shadow: ${boxShadowStandard || formBoxShadow};
    `;
}}
  border-radius: ${token('theme.dimension.border.radius.standard', borderRadiusStandard)};
  background-color: ${token('theme.color.bg.surface.prominent', colorWhite)};
`;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const InfoBoxTip = styled(_ref4 => {
  let {
    tipLocation,
    ...rest
  } = _ref4;
  return /*#__PURE__*/_jsx("div", {
    "data-testid": "info-box-tip",
    ...rest
  });
})`
  position: relative;
  z-index: 1;
  width: 24px;
  height: 15px;
  ${_ref5 => {
  let {
    tipLocation
  } = _ref5;
  return tipLocation && getTipAlignment(tipLocation);
}};

  &::after {
    content: '';
    display: block;
    position: absolute;
    inset-block-end: 0;
    border-inline-start: 12px solid transparent;
    border-inline-end: 12px solid transparent;
    border-block-start: 15px solid transparent;
    border-block-end: 15px solid ${token('theme.color.bg.surface.prominent', colorWhite)};
  }
`;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const IconColumn = styled(_ref6 => {
  let {
    isSystemIcon,
    ...rest
  } = _ref6;
  return /*#__PURE__*/_jsx(Column, {
    ...rest
  });
})`
  align-self: flex-start;
  font-size: 0;
  line-height: 1;

  /* If iconAndBorderColor is provided then this does nothing. */
  ${_ref7 => {
  let {
    isSystemIcon
  } = _ref7;
  return isSystemIcon && css`
      color: ${token('theme.color.fg.brand', colorBrandBase)};
    `;
}}
`;
export const InfoBoxContentContainer = styled.div`
  text-wrap: pretty;
`;
const InfoBox = props => {
  var _Icon_displayName;
  const {
    children,
    title,
    titleComponent = 'h3',
    withTip = false,
    tipLocation = 'left',
    withBorder = true,
    iconAndBorderColor,
    withBoxShadow = true,
    withIcon = true,
    icon: Icon = InfoSystemIcon,
    ...otherProps
  } = props;
  const isSystemIcon = Icon && ((_Icon_displayName = Icon.displayName) === null || _Icon_displayName === void 0 ? void 0 : _Icon_displayName.includes('System'));
  return /*#__PURE__*/_jsxs("div", {
    children: [withTip && /*#__PURE__*/_jsx(InfoBoxTip, {
      tipLocation: tipLocation
    }), /*#__PURE__*/_jsx(InfoBoxContainer, {
      withBorder: withBorder,
      iconAndBorderColor: iconAndBorderColor,
      withBoxShadow: withBoxShadow,
      ...otherProps,
      "data-mesh-component": "INFO-BOX",
      children: /*#__PURE__*/_jsxs(Columns, {
        verticalAlign: "center",
        space: {
          xs: 4,
          mini: 5
        },
        collapseBelow: "mini",
        children: [withIcon && /*#__PURE__*/_jsx(IconColumn, {
          width: "content",
          isSystemIcon: isSystemIcon,
          children: Icon && /*#__PURE__*/_jsx(Box, {
            width: "2rem",
            display: "flex",
            justifyContent: "center",
            children: /*#__PURE__*/_jsx(Icon, {
              size: isSystemIcon ? 'sm' : 'md',
              fill: iconAndBorderColor
            })
          })
        }), /*#__PURE__*/_jsx(Column, {
          children: /*#__PURE__*/_jsxs(Stack, {
            space: 2,
            children: [title && /*#__PURE__*/_jsx(Heading, {
              size: 4,
              component: titleComponent,
              children: title
            }), /*#__PURE__*/_jsx(InfoBoxContentContainer, {
              children: children
            })]
          })
        })]
      })
    })]
  });
};
InfoBox.displayName = 'InfoBox';
export default InfoBox;